import type { Config } from 'dompurify';

import DOMPurify from 'dompurify';

/** The selector of a module cell in a converted diagram: a group with the cell's module id. */
export const DIAGRAM_CELL_SELECTOR = 'g.can-module';

/** The attribute a cell's full name moves to when its title element is taken off the live drawing. */
const TITLE_ATTRIBUTE = 'data-title';

/** A module cell of an injected diagram. */
export type DiagramCell = DiagramNames & {
    /** The cell's group in the document. */
    element: SVGGElement;
    /** The id of the cell, the module's id. */
    id: string;
};

/** The names a cell gives its module. */
export type DiagramNames = {
    /** The abbreviation the cell shows, e.g. "TCU / ISS"; the id when it shows none. */
    label: string;
    /** The full name of the module, as the cell carries it for its tooltip; the id when it carries none. */
    title: string;
};

/**
 * A sanitizer of its own for the diagrams, so the hook the shared one adds to links never runs on them.
 * The SVG profile keeps ids, classes, data attributes and titles and drops scripts, event attributes and
 * foreign content; style sheets inside a file are dropped too, since the page paints the cells itself.
 */
const purifier = DOMPurify(window);

const DIAGRAM_CONFIG: Config = {
    FORBID_TAGS: ['style'],
    USE_PROFILES: { svg: true, svgFilters: true },
};

/**
 * Take the title elements off the cells of an injected diagram, keeping each full name on its cell: a
 * browser shows an SVG title as a tooltip of its own, next to the one the page draws. A cell whose title
 * was already taken off is left as it is.
 *
 * @param root - the element the diagram was injected in
 */
export function detachDiagramTitles(root: ParentNode): void {
    for (const element of root.querySelectorAll<SVGGElement>(DIAGRAM_CELL_SELECTOR)) {
        const title = element.querySelector('title');

        if (title !== null) {
            element.setAttribute(TITLE_ATTRIBUTE, titleText(title, element.id));
            title.remove();
        }
    }
}

/**
 * Find every module cell of an injected diagram.
 *
 * @param root - the element the diagram was injected in
 * @returns the cells, in document order
 */
export function findDiagramCells(root: ParentNode): DiagramCell[] {
    return Array.from(root.querySelectorAll<SVGGElement>(DIAGRAM_CELL_SELECTOR), toCell);
}

/**
 * Read the names every module cell of a diagram gives its module, by the id of the cell.
 *
 * @param svg - the SVG text as read from its file
 * @returns the label and full name by cell id, in document order
 */
export function readDiagramNames(svg: string): ReadonlyMap<string, DiagramNames> {
    const root = document.createElement('div');
    root.innerHTML = sanitizeDiagramSvg(svg);

    return new Map(findDiagramCells(root).map(({ id, label, title }) => [id, { label, title }]));
}

/**
 * Sanitize the SVG text of a diagram before it is injected.
 *
 * @param svg - the SVG text as read from its file
 * @returns the SVG text with only drawing content left
 */
export function sanitizeDiagramSvg(svg: string): string {
    return purifier.sanitize(svg, DIAGRAM_CONFIG);
}

/**
 * The module cell an event happened in, if any.
 *
 * @param target - the event target
 * @returns the cell the target is or sits inside, or undefined outside every cell
 */
export function toDiagramCell(target: EventTarget | null): DiagramCell | undefined {
    const element = target instanceof Element ? target.closest<SVGGElement>(DIAGRAM_CELL_SELECTOR) : null;

    return element === null ? undefined : toCell(element);
}

/**
 * The full name a cell gives its module: the one kept on the cell once its title element was taken off,
 * else the title element's text; the id when the cell has neither or the title is blank.
 *
 * @param element - the cell's group
 * @returns the full name
 */
function cellTitle(element: SVGGElement): string {
    const kept = element.getAttribute(TITLE_ATTRIBUTE);

    if (kept !== null) {
        return kept;
    }

    const title = element.querySelector('title');

    return title === null ? element.id : titleText(title, element.id);
}

/**
 * The text of a title element, or the id when it is blank.
 *
 * @param title - the title element
 * @param id - the id of its cell
 * @returns the trimmed text, or the id
 */
function titleText(title: Element, id: string): string {
    return title.textContent?.trim() || id;
}

/**
 * Read a cell from its group.
 *
 * @param element - the cell's group
 * @returns the cell
 */
function toCell(element: SVGGElement): DiagramCell {
    return {
        element,
        id: element.id,
        label: element.querySelector('text')?.textContent?.trim() || element.id,
        title: cellTitle(element),
    };
}
